import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useDispatch, useStore } from "react-redux";
import { createWorkspaceArtistApi, getWorkspaceArtistsApi, getWorkspaceSummaryApi, switchArtistContextApi } from "../../api/organization.api";
import { showAdminToast } from "../../helper/ShowToast";
import { setAuthToken, setUserDetails } from "../../redux/actions/auth";
import { refreshWorkspaceSession } from "../../redux/actions/billing";
import { invalidateUrStudioQueries } from "../urstudio/urstudioScope";

const ARTIST_SCOPED_QUERY_ROOTS = [
    "workspace-artists",
    "artistTracks",
    "artistTrackSearch",
    "albumPlaylistTracks",
    "smartlink-list",
    "smartlink-analytics",
    "smartlink-details",
    "top-three-smartlinks",
    "overall-smartlink-analytics",
    "overall-audience",
    "artist-future-save",
    "biolink-status",
    "platform-stats-by-source",
    "platform-charts-by-source",
    "meta-campaign-list",
    "meta-campaign-history",
    "meta-campaign-details",
    "meta-campaign-analytics",
    "meta-campaign-audience",
];

export const useWorkspaceSummary = () => {
    return useQuery({
        queryKey: ["workspace-summary"],
        queryFn: getWorkspaceSummaryApi,
        staleTime: 60 * 1000,
        retry: false,
    });
};

export const useWorkspaceArtists = () => {
    return useQuery({
        queryKey: ["workspace-artists"],
        queryFn: getWorkspaceArtistsApi,
        staleTime: 60 * 1000,
        retry: false,
    });
};

export const useCreateWorkspaceArtist = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (payload) => createWorkspaceArtistApi(payload),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["workspace-artists"] });
            queryClient.invalidateQueries({ queryKey: ["workspace-summary"] });
            showAdminToast("success", "Artist saved. Complete payment to activate this seat.");
        },
        onError: (error) => {
            const message = error?.response?.data?.message || error?.message || "Could not add artist";
            showAdminToast("error", message);
        },
    });
};

export const useSwitchArtist = () => {
    const dispatch = useDispatch();
    const store = useStore();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ artistProfileId }) => {
            const data = await switchArtistContextApi(artistProfileId);
            const token = data?.token;
            if (token) {
                localStorage.setItem("authToken", token);
                dispatch(setAuthToken(token));
            }

            try {
                await dispatch(refreshWorkspaceSession());
                const songstatsId = store.getState()?.auth?.access?.artist?.songstatsArtistId || "";
                dispatch(setUserDetails({ assignedArtistSongstateId: songstatsId }));
            } catch {
                // The artist is already saved. Access and billing refresh on the next page load.
            }

            invalidateUrStudioQueries(queryClient);
            ARTIST_SCOPED_QUERY_ROOTS.forEach((root) => {
                queryClient.invalidateQueries({ queryKey: [root] });
            });
            return data;
        },
        onSuccess: (_data, variables) => {
            const name = variables?.name;
            showAdminToast("success", name ? `Switched to ${name}` : "Artist switched");
        },
        onError: (error) => {
            const message = error?.response?.data?.message || error?.message || "Could not switch artist";
            showAdminToast("error", message);
        },
    });
};
